import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'
import axios from 'axios'
import { useQueryClient } from '@tanstack/react-query'
import { SessionContext } from './SessionContext'
import { allNetworks } from 'views/calendar/SocialNetworkSidePanel'
import { proxyUrl } from 'utils/proxy'
import Resizer from 'react-image-file-resizer'
import { AI_STUDIO_PLANS, SOCIAL_PLANS } from 'views/subscription'
import { api } from 'api/http'
import { queryKeys } from 'api/queryKeys'
import { invalidateCompanyScopedQueries } from 'api/queryClient'
import {
    useCompany as useCompanyQuery,
    useCompanyUsers as useCompanyUsersQuery,
    useCreateCompany,
    useUpdateCompany as useUpdateCompanyMutation,
    useDeleteCompany as useDeleteCompanyMutation,
} from 'api/hooks/useCompanies'
import { useCompanySocialNetworks } from 'api/hooks/useSocialNetworks'

/**
 * Compatibility shim around the React Query company hooks. The consumer
 * interface (`company`, `setCompany`, `companyUsers`, `companySocials`,
 * `updateSocials`, `updateCompanyUsers`, `updateCompany`, `mainUser`,
 * `subscriptionPlanName`, `mainUserPlanType`, `roles`, `isSystemAdmin`,
 * `createCompany`, `deleteCompany`, `getCompany`, `getCompanyUsers`,
 * `updateCompanyLogo`, `updateCompanyLogoUrl`) is preserved verbatim so the
 * ~230 call sites can be migrated incrementally.
 */

const CompanyContext = React.createContext({
    company: undefined,
    setCompany: () => {},
    isSystemAdmin: false,
    companyUsers: [],
    companySocials: [],
    isLoadingCompanySocials: true,
    updateSocials: () => {},
    updateCompanyUsers: () => {},
    updateCompanyLogoUrl: () => {},
    mainUser: undefined,
    subscriptionPlanName: undefined,
    getCompanyUsers: () => {},
    getCompany: () => {},
    mainUserPlanType: null,
})

const FAKE_PLAN_KEY = 'devSubscriptionPlanName'
const readFakeSubscriptionPlanName = () => {
    if (typeof window === 'undefined') return undefined
    if (process.env.NODE_ENV === 'production') return undefined
    try {
        const params = new URL(window.location.href).searchParams
        if (params.has('clearFakePlan')) {
            localStorage.removeItem(FAKE_PLAN_KEY)
            return undefined
        }
        if (params.has('fakePlan')) {
            const value = params.get('fakePlan') || ''
            localStorage.setItem(FAKE_PLAN_KEY, value)
            return value
        }
        const stored = localStorage.getItem(FAKE_PLAN_KEY)
        return stored === null ? undefined : stored
    } catch (_) {
        return undefined
    }
}

const resizeFile = (file) =>
    new Promise((resolve) => {
        Resizer.imageFileResizer(
            file,
            512,
            512,
            file.type.includes('png') ? 'PNG' : 'JPEG',
            100,
            0,
            (uri) => resolve(uri),
            'file'
        )
    })

const readActiveCompanyId = (userId) => {
    if (!userId) return undefined
    try {
        const raw = localStorage.getItem(`actualCompanyID${userId}`)
        // Only honor a numeric id. Legacy/corrupt values such as
        // "[object Object]" are ignored so a poisoned key self-heals on boot.
        return raw && /^\d+$/.test(raw) ? raw : undefined
    } catch (_) {
        return undefined
    }
}

const CompanyProvider = ({ children }) => {
    const { user, session, updateBalance } = useContext(SessionContext)
    const qc = useQueryClient()
    const fakePlanName = readFakeSubscriptionPlanName()

    const companies = user?.companies

    // Active company id (persisted to localStorage); the `company` object is
    // derived from `user.companies` so we never hold stale fields.
    const [activeCompanyId, setActiveCompanyId] = useState(() =>
        readActiveCompanyId(user?.id)
    )

    // Refresh activeCompanyId when the user loads (boot) or changes.
    useEffect(() => {
        if (!user?.id) return
        const stored = readActiveCompanyId(user.id)
        const valid =
            stored && companies?.find((c) => String(c.id) === String(stored))
                ? stored
                : companies?.[0]?.id
        if (valid && String(valid) !== String(activeCompanyId)) {
            setActiveCompanyId(valid)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id, companies?.length])

    // Persist + invalidate caches on switch.
    useEffect(() => {
        if (!user?.id) return
        if (window.location.pathname === '/onboarding') {
            if (!activeCompanyId) {
                try {
                    localStorage.removeItem(`actualCompanyID${user.id}`)
                } catch (_) {}
            }
            return
        }
        // Only ever persist a numeric id, never a stringified object/function.
        if (activeCompanyId && /^\d+$/.test(String(activeCompanyId))) {
            try {
                localStorage.setItem(
                    `actualCompanyID${user.id}`,
                    String(activeCompanyId)
                )
            } catch (_) {}
        }
    }, [activeCompanyId, user?.id])

    // The active company object: prefer the row in `user.companies` for fields
    // like `role`, fall back to the dedicated /companies/{id} fetch.
    const companyFromUser = useMemo(
        () =>
            companies?.find(
                (c) => String(c.id) === String(activeCompanyId)
            ),
        [companies, activeCompanyId]
    )

    const { data: companyDetail } = useCompanyQuery(
        activeCompanyId,
        { include: ['image', 'role', 'user', 'user.image'] },
        {
            enabled: !!activeCompanyId && !companyFromUser,
            staleTime: 60_000,
        }
    )

    const company = companyFromUser || companyDetail

    // Company users
    const { data: companyUsersRaw } = useCompanyUsersQuery(
        activeCompanyId,
        {
            include: [
                'image',
                'roleUsers',
                'roleUsers.role',
                'roleUsers.company',
                'subscriptionTokens',
                'appSumoLicense',
                'subscriptions.items.product',
                'whiteLabel',
            ],
        },
        {
            enabled: !!activeCompanyId,
            select: (resp) =>
                (resp?.data ?? resp ?? []).map((u) => ({
                    ...u,
                    subscription:
                        u?.subscriptions?.find(
                            (s) =>
                                s?.stripe_status === 'active' ||
                                s?.stripe_status === 'trialing'
                        ) || undefined,
                })),
        }
    )
    const companyUsers = companyUsersRaw || []

    // Company social networks
    const { data: companySocialsRaw, isLoading: isLoadingCompanySocials } =
        useCompanySocialNetworks(
            {
                companyId: activeCompanyId,
                include: ['image.thumbnail'],
            },
            {
                enabled: !!activeCompanyId && session?.isLoggedIn,
                staleTime: 60_000,
                select: (resp) => {
                    const list = resp?.data ?? resp ?? []
                    return list
                        .filter((e) => allNetworks.includes(e.type))
                        .map((e) => ({ ...e, company_id: activeCompanyId }))
                },
            }
        )
    const companySocials = companySocialsRaw || []

    // Keep localStorage mirror up to date (legacy: some code reads it on boot).
    useEffect(() => {
        if (!activeCompanyId) return
        try {
            localStorage.setItem(
                `companySocials${activeCompanyId}`,
                JSON.stringify(companySocials)
            )
        } catch (_) {}
        try {
            if (companySocials?.length) {
                window.Intercom?.('update', { has_social_network: true })
            } else {
                window.Intercom?.('update', { has_social_network: false })
            }
        } catch (_) {}
    }, [activeCompanyId, companySocials])

    // Update balance when company switches (mirrors legacy behavior).
    useEffect(() => {
        if (activeCompanyId) updateBalance?.()
    }, [activeCompanyId, updateBalance])

    // Honor ?company_id=N in the URL: switch to that company once.
    useEffect(() => {
        if (!companies?.length) return
        try {
            const url = new URL(window.location.href)
            const fromUrl = url.searchParams.get('company_id')
            if (
                fromUrl &&
                companies.find((c) => String(c.id) === String(fromUrl))
            ) {
                url.searchParams.delete('company_id')
                window.history.replaceState({}, '', url.toString())
                setActiveCompanyId(fromUrl)
            }
        } catch (_) {}
    }, [companies])

    // ─── mainUser (company owner, enriched with subscription) ───────────────
    // Prefer the enriched row from /users (has subscription pre-resolved).
    // Fall back to the lightweight `company.user` ref (from /me include) while
    // the company-users query is still loading. In both cases, when the
    // company owner is the logged-in user, mirror subscription/payment state
    // from the session user — that data is already live there. Waiting for
    // /users to load would briefly leave mainUser without a subscription,
    // which would trip the subscription modal in components/layout/index.js.
    const mainUser = useMemo(() => {
        if (!company?.id) return undefined
        const fromCompanyUsers = companyUsers?.find(
            (u) => u.id == company?.user?.id
        )
        const base = fromCompanyUsers ?? company?.user
        if (!base) return undefined
        if (user?.id && user.id == base.id) {
            return {
                ...base,
                subscription: user?.subscription,
                ...(user?.pm_last_four !== undefined && {
                    pm_last_four: user.pm_last_four,
                }),
                ...(user?.pm_type !== undefined && {
                    pm_type: user.pm_type,
                }),
            }
        }
        return base
    }, [
        company?.id,
        company?.user,
        companyUsers,
        user?.id,
        user?.subscription,
        user?.pm_last_four,
        user?.pm_type,
    ])

    const subscriptionPlanName = useMemo(() => {
        if (fakePlanName !== undefined) return fakePlanName
        return (
            mainUser?.subscription?.items?.[0]?.product?.name || 'free'
        )
    }, [mainUser?.subscription, fakePlanName])

    const mainUserPlanType = useMemo(() => {
        const planName = mainUser?.subscription?.items?.find(
            (item) => item.product?.type === 'plan'
        )?.product?.name
        if (AI_STUDIO_PLANS.includes(planName)) return 'ai_studio'
        if (SOCIAL_PLANS.includes(planName)) return 'social'
        return null
    }, [mainUser?.subscription?.items])

    // ─── Public mutators (preserve legacy shape) ────────────────────────────
    const setCompany = useCallback(
        (next) => {
            // Legacy callers may pass a functional updater
            // (setCompany(prev => ({ ...prev, ...changes }))). Resolve it against
            // the current company object first — otherwise the function is handed
            // to setActiveCompanyId, React runs it as a state updater, and
            // `activeCompanyId` ends up holding an object that serializes to
            // "[object Object]" in the company URL.
            const resolved = typeof next === 'function' ? next(company) : next
            if (!resolved) {
                setActiveCompanyId(undefined)
                return
            }
            const nextId = typeof resolved === 'object' ? resolved.id : resolved
            // Only ever hold a numeric id; guard against objects/functions/garbage.
            if (nextId == null || !/^\d+$/.test(String(nextId))) return
            if (String(nextId) === String(activeCompanyId)) return
            invalidateCompanyScopedQueries()
            setActiveCompanyId(nextId)
        },
        [activeCompanyId, company]
    )

    const updateSocials = useCallback(() => {
        if (!activeCompanyId) return
        qc.invalidateQueries({
            queryKey: queryKeys.socialNetworks.all,
        })
    }, [activeCompanyId, qc])

    const updateCompanyUsers = useCallback(() => {
        if (!activeCompanyId) return
        qc.invalidateQueries({ queryKey: queryKeys.users.all })
    }, [activeCompanyId, qc])

    const { mutateAsync: updateCompanyMutateAsync } = useUpdateCompanyMutation()
    const { mutateAsync: createCompanyMutateAsync } = useCreateCompany()
    const { mutateAsync: deleteCompanyMutateAsync } = useDeleteCompanyMutation()

    const updateCompanyLogoUrl = useCallback(
        async (url) => {
            if (!activeCompanyId) return
            try {
                const blob = await fetch(
                    url.startsWith('data:image') ? url : proxyUrl(url)
                ).then((res) => res.blob())
                const imageType = !blob?.type?.split('/')?.[1]
                    ? url?.includes('data:image/')
                        ? url?.substring(
                              'data:image/'?.length,
                              url.indexOf(';base64')
                          )
                        : url?.split('.')?.pop()?.toLowerCase()
                    : blob?.type?.split('/')?.[1]
                const name = `logo.${imageType}`
                const file = new File([blob], name, {
                    type: `image/${imageType}`,
                })
                const type = file.type.split('/')[0]
                const response = await api.post(
                    `/companies/${activeCompanyId}/image`,
                    {},
                    { filename: name, type, visibility: 'public' }
                )
                const aws_uri = response?.data?.presigned_url
                if (aws_uri) {
                    await axios.put(aws_uri, file, {
                        headers: {
                            'Content-Type': file.type,
                            'x-ms-blob-type': 'BlockBlob',
                        },
                    })
                }
                qc.invalidateQueries({ queryKey: queryKeys.companies.all })
            } catch (err) {
                console.error(err)
            }
        },
        [activeCompanyId, qc]
    )

    const updateCompany = useCallback(
        async (payload) => {
            if (!activeCompanyId) return
            const response = await updateCompanyMutateAsync({
                id: activeCompanyId,
                ...payload,
            })
            if (payload?.image?.url) {
                await updateCompanyLogoUrl(payload.image.url)
            }
            // Force a refresh of the session-user companies tree so the
            // mutated company is reflected in `user.companies`.
            qc.invalidateQueries({ queryKey: queryKeys.session.me })
            return response?.data ?? response
        },
        [activeCompanyId, updateCompanyMutateAsync, updateCompanyLogoUrl, qc]
    )

    const createCompany = useCallback(
        async (payload) => {
            const response = await createCompanyMutateAsync(payload)
            return response?.data ?? response
        },
        [createCompanyMutateAsync]
    )

    const deleteCompany = useCallback(
        async (companyId) => {
            await deleteCompanyMutateAsync(companyId)
        },
        [deleteCompanyMutateAsync]
    )

    const updateCompanyLogo = useCallback(
        async (file, companyIdArg = undefined) => {
            const cid = companyIdArg || activeCompanyId
            if (!cid) return
            const resized = await resizeFile(file)
            const type = resized.type.includes('image') ? 'image' : 'video'
            const response = await api.post(
                `/companies/${cid}/image`,
                {},
                { filename: resized.name, type, visibility: 'public' }
            )
            const aws_uri = response?.data?.presigned_url
            if (aws_uri) {
                await axios.put(aws_uri, resized, {
                    headers: {
                        'Content-Type': resized.type,
                        'x-ms-blob-type': 'BlockBlob',
                    },
                })
            }
            qc.invalidateQueries({ queryKey: queryKeys.companies.all })
        },
        [activeCompanyId, qc]
    )

    const getCompany = useCallback(
        async (companyId) => {
            if (!companyId) return undefined
            return qc.fetchQuery({
                queryKey: queryKeys.companies.detail(companyId, {}),
                staleTime: 60_000,
                queryFn: async ({ signal }) => {
                    const response = await api.get(
                        `/companies/${companyId}`,
                        undefined,
                        { signal }
                    )
                    return response?.data
                },
            })
        },
        [qc]
    )

    const getCompanyUsers = useCallback(
        async (companyId) => {
            if (!companyId) return []
            return qc.fetchQuery({
                queryKey: queryKeys.users.list({
                    companyId: String(companyId),
                    scope: 'companyUsers',
                }),
                staleTime: 60_000,
                queryFn: async ({ signal }) => {
                    const response = await api.get(
                        '/users',
                        {
                            'filter[companies.id]': companyId,
                            include: [
                                'image',
                                'roleUsers.role',
                                'roleUsers.company',
                                'subscriptionTokens',
                                'subscriptions.items.product',
                            ].join(','),
                        },
                        { signal }
                    )
                    return (response?.data ?? []).map((u) => ({
                        ...u,
                        subscription:
                            u?.subscriptions?.find(
                                (s) =>
                                    s?.stripe_status === 'active' ||
                                    s?.stripe_status === 'trialing'
                            ) || undefined,
                    }))
                },
            })
        },
        [qc]
    )

    const value = useMemo(
        () => ({
            company,
            setCompany,
            roles: [
                company?.role,
                ...(mainUser?.id == user?.id ? [{ name: 'owner' }] : []),
                ...(user?.roleUsers?.find((e) => e?.role?.name === 'sysadmin')
                    ? [{ name: 'sysadmin' }]
                    : []),
            ],
            isSystemAdmin: !!user?.roleUsers?.find(
                (e) => e?.role?.name === 'sysadmin'
            ),
            companyUsers,
            companySocials,
            isLoadingCompanySocials: !!isLoadingCompanySocials,
            updateSocials,
            updateCompanyUsers,
            updateCompanyLogoUrl,
            updateCompany,
            mainUser,
            subscriptionPlanName,
            mainUserPlanType,
            updateCompanyLogo,
            createCompany,
            deleteCompany,
            getCompanyUsers,
            getCompany,
        }),
        [
            company,
            setCompany,
            mainUser,
            user?.id,
            user?.roleUsers,
            companyUsers,
            companySocials,
            isLoadingCompanySocials,
            updateSocials,
            updateCompanyUsers,
            updateCompanyLogoUrl,
            updateCompany,
            subscriptionPlanName,
            mainUserPlanType,
            updateCompanyLogo,
            createCompany,
            deleteCompany,
            getCompanyUsers,
            getCompany,
        ]
    )

    return (
        <CompanyContext.Provider value={value}>
            {children}
        </CompanyContext.Provider>
    )
}

export { CompanyContext, CompanyProvider }
